import * as React from 'react';
import { AxiosPromise } from 'axios';

import { DataStorage } from "../../../../helpers/utils/DataStorage";
import { ModelAction } from "../../../../types/responses/ModelAction";
import { ProjectDocFolderAttachment } from '../../models/ProjectDocFolderAttachment';
import { ProjectDoc } from '../../models/ProjectDoc';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from "../../../../helpers/hooks/useBackgroundPromiseWrapper";
import { useDownload } from "../../../../helpers/hooks/useDownload";
import { ProjectDocFolderWorkflowInstanceAttachment } from '../../models/ProjectDocFolderWorkflowInstanceAttachment';
import { FileRow, FileTableHeader, FileTableColumns } from '../../../../components/synced/ui/FileRow/FileRow';
import { EmptyState } from '../../../../components/synced/ui/EmptyState/EmptyState';
import { useIsMobile } from '../../../../helpers/hooks/useIsMobile';
import { constantFormat } from '../../../../helpers/constants';
import { NoteUpdatesController } from '../../../notes/helpers/noteUpdates';
import {
  ContractorViewerModal,
  isPdfAttachment,
  isWordAttachment,
} from '../../../notes/components/viewer/PDFViewerModal';


interface PrevIterationFileUploadComponentProps {
  actions: ModelAction[];
  projectDocFolderId: number;
  onFilesLoaded: (count: number) => void;
  dataStorage: DataStorage;
  setDataStorageValue: (key: string, value: any) => void;
  // Нужны, чтобы открыть файл в просмотрщике замечаний и посчитать,
  // какие действия по замечанию доступны контрагенту.
  projectDocFolderStatusId?: number;
  projectDoc?: ProjectDoc;
  // Источник файлов. По умолчанию — все предыдущие итерации (архив).
  fetchFiles?: (folderId: number) => AxiosPromise<ProjectDocFolderAttachment[]>;
  // Текст пустого состояния и суффикс ключа кэша — чтобы две вкладки
  // (архив / предыдущее согласование) не делили один список в DataStorage.
  emptyText?: string;
  storageKey?: string;
  noteUpdates?: NoteUpdatesController;
}

export const PrevIterationFileUploadComponent: React.FC<PrevIterationFileUploadComponentProps> = ({ actions, projectDocFolderId, onFilesLoaded, dataStorage, setDataStorageValue, projectDocFolderStatusId, projectDoc, fetchFiles, emptyText, storageKey, noteUpdates }) => {
  const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();
  const { downloadAttachment, isDownloading } = useDownload();
  // Файл, открытый в просмотрщике замечаний. Доступен, только если известен
  // статус папки — иначе нечем посчитать права контрагента на замечания.
  const [viewTarget, setViewTarget] = React.useState<ProjectDocFolderAttachment | null>(null);
  const canOpenViewer = projectDocFolderStatusId != null;
  const isMobile = useIsMobile(768);
  const fileColumns: FileTableColumns = isMobile
    ? { template: `1fr 60px`, showDate: false }
    : { template: `1fr 160px 60px`, showDate: true };

  const folderAttachmentsKey: string = `FileUploadComponent_folderAttachments_${storageKey ?? 'archive'}`;
  const [folderAttachments, setFolderAttachments] = React.useState<ProjectDocFolderAttachment[]>(dataStorage.get<ProjectDocFolderAttachment[]>(folderAttachmentsKey, []));

  const [currentProjectDocFolderId, setCurrentProjectDocFolderId] = React.useState<number>(projectDocFolderId);
  React.useEffect(() => {
    if (currentProjectDocFolderId === projectDocFolderId)
      return;

    setFolderAttachments([]);
    setDataStorageValue(folderAttachmentsKey, undefined);
    onFilesLoaded(0);
    setCurrentProjectDocFolderId(projectDocFolderId);
    // eslint-disable-next-line
  }, [projectDocFolderId]);


  const loadfiles = React.useCallback((force: boolean = true) => {
    if (!force && dataStorage.hasValue(folderAttachmentsKey)) {
      onFilesLoaded(folderAttachments.length);
      return;
    }

    if (currentProjectDocFolderId === 0) {
      setFolderAttachments([]);
      setDataStorageValue(folderAttachmentsKey, []);
      return;
    }

    promiseWrapper(
      () => (fetchFiles ?? services.pDFAttachmentsService.prevInstanceFetch.bind(services.pDFAttachmentsService))(currentProjectDocFolderId),
      (res: ProjectDocFolderWorkflowInstanceAttachment[]) => {
        var mappedRes: ProjectDocFolderAttachment[] = [];
        res.forEach(x => {
          if (x.ProjectDocFolderAttachment != null) {
            mappedRes.push(x.ProjectDocFolderAttachment);
          }
        });
        setFolderAttachments(mappedRes);
        setDataStorageValue(folderAttachmentsKey, mappedRes);
        onFilesLoaded(mappedRes.length);
      }
    );
    // eslint-disable-next-line
  }, [currentProjectDocFolderId, dataStorage, folderAttachmentsKey, setDataStorageValue, onFilesLoaded, folderAttachments.length, fetchFiles]);
  // eslint-disable-next-line
  React.useEffect(loadfiles, [currentProjectDocFolderId]);

  const fileDownloadHandler = (attachmentId: string) => {
    void downloadAttachment(attachmentId);
  };

  if (isLoading || (currentProjectDocFolderId > 0 && !dataStorage.hasValue(folderAttachmentsKey) && folderAttachments.length === 0)) {
    return (
      <div className="block-loading-host block-loading-host--loading files-loading-empty">
        <div className="block-loading-overlay" aria-busy="true" aria-live="polite">
          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
          <span>Загрузка…</span>
        </div>
      </div>
    );
  }

  if (folderAttachments.length === 0) {
    return <EmptyState icon="fa-folder-open" title={emptyText ?? "Нет файлов предыдущих итераций"} />;
  }

  return (
    <div className="files-table">
      {viewTarget && projectDocFolderStatusId != null && (
        <ContractorViewerModal
          attachment={viewTarget}
          projectDocFolderId={projectDocFolderId}
          projectDocFolderStatusId={projectDocFolderStatusId}
          projectDoc={projectDoc}
          onHide={() => setViewTarget(null)}
          {...(noteUpdates ? { noteUpdates } : {})} />
      )}
      <FileTableHeader columns={fileColumns} />
      {folderAttachments.map(att => {
        const title = att.Attachment?.Title ?? att.Attachment?.FileName ?? '';
        const created = att.Attachment?.Created;
        const dateStr = created ? constantFormat.DateToString(constantFormat.StringToDate(created as unknown as string)) : '';
        // PDF/Word с известным статусом папки открываются во вьювере замечаний
        // по клику на строку; скачивание для них — отдельная явная кнопка.
        const canView = canOpenViewer && (isPdfAttachment(att) || isWordAttachment(att));
        return (
          <FileRow
            key={att.Id}
            columns={fileColumns}
            name={title}
            extSource={att.Attachment?.FileName ?? title}
            date={dateStr}
            onClick={canView ? () => setViewTarget(att) : () => fileDownloadHandler(att.AttachmentId)}
            actionsSlot={
              <button
                type="button"
                title={isDownloading ? `Скачивание…` : `Скачать`}
                className="files-delete-btn"
                disabled={isDownloading}
                onClick={(e) => {
                  e.stopPropagation();
                  fileDownloadHandler(att.AttachmentId);
                }}>
                <i className={isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`} />
              </button>
            } />
        );
      })}
    </div>
  );
}
